import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import SideBar from './SideBar';
import { useIsPhone } from '../../hooks/useMediaQuery';
import { useBackGuard } from '../../hooks/useBackGuard';
import { ROUTES } from '../../config/constants';

/**
 * Application shell: navigation rail | list sidebar | routed pane.
 *
 * Every page used to re-declare this scaffolding. It now lives here once and
 * routes render only their own pane through <Outlet />.
 *
 * The shell owns the state that genuinely spans the sidebar and the pane — the
 * right-hand panel flags a sidebar flips but a pane renders, the current
 * selection for the list/detail sections, and on a phone, whether the pane is
 * covering the list at all. Page-local state stays in the page.
 */

const ShellContext = createContext(null);

/** Inert handles, so a consumer rendered outside a shell never has to null-check. */
const NO_SHELL = Object.freeze({
  isPhone: false,
  paneOpen: true,
  openPane: () => {},
  closePane: () => {},
  rightSideOpen: false,
  setRightSideOpen: () => {},
  rightSideOpen1: false,
  setRightSideOpen1: () => {},
  rightSideOpen2: false,
  setRightSideOpen2: () => {},
  selectedCall: null,
  setSelectedCall: () => {},
  selectedNotification: null,
  setSelectedNotification: () => {},
  selectedSaved: null,
  setSelectedSaved: () => {},
});

export const useShell = () => useContext(ShellContext) ?? NO_SHELL;

/**
 * Does this path name a specific thing, rather than a section landing?
 *
 * On a phone the pane covers the list, so it may only do that when there is
 * something to show. A conversation and a settings section are both named by
 * the URL — which is what makes a deep link or a refresh land on the pane
 * instead of the list. Calls, notifications and saved messages are not: their
 * tabs are routes but the selected record is state, so those come from the
 * selection below.
 */
const routeNamesDetail = (pathname) =>
  /^\/messages\/.+/.test(pathname) ||
  /^\/settings\/.+/.test(pathname) ||
  // A note is named by its URL for the same reason a conversation is: it has to survive a
  // reload and be reachable from a shared link. Without it here the notes pane never counts
  // as "open", so on a phone — where the pane is `display: none` until it does — opening a
  // note showed the list with nothing happening.
  /^\/notes\/.+/.test(pathname);

/**
 * Panes whose identity lives in the URL, so leaving one is a NAVIGATION rather than a
 * state change. Dismissing in state would leave the address bar pointing at a pane that is
 * no longer on screen — and a reload would bring it straight back.
 */
const isConversationRoute = (pathname) => /^\/messages\/.+/.test(pathname);
const isNoteRoute = (pathname) => /^\/notes\/.+/.test(pathname);
const isUrlAddressedPane = (pathname) => isConversationRoute(pathname) || isNoteRoute(pathname);

const ChatShell = ({ Sidebar }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isPhone = useIsPhone();

  // Right-hand panels. Flipped from a sidebar or a pane header, consumed by
  // the pane — so they have to live above both.
  const [rightSideOpen, setRightSideOpen] = useState(false);
  const [rightSideOpen1, setRightSideOpen1] = useState(false);
  const [rightSideOpen2, setRightSideOpen2] = useState(false);

  // The calls list lives in the sidebar and its detail renders in the pane,
  // so the current selection has to sit above both. Notifications work the same
  // way.
  const [selectedCall, setSelectedCall] = useState(null);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [selectedSaved, setSelectedSaved] = useState(null);

  /**
   * Which detail the pane is showing. Dismissal is recorded against this key
   * rather than as a plain boolean: picking a different conversation, tab or
   * record produces a new key and therefore reopens the pane on its own, so
   * every sidebar does not have to remember to reset a flag. `openPane` covers
   * the one case the key cannot — re-picking the record that was just closed.
   */
  const detailKey = `${location.pathname}|${selectedCall?.id ?? ''}|${selectedNotification?.id ?? ''}|${selectedSaved?.id ?? ''}`;
  const hasDetail =
    routeNamesDetail(location.pathname) ||
    Boolean(selectedCall) ||
    Boolean(selectedNotification) ||
    Boolean(selectedSaved);

  const [dismissedKey, setDismissedKey] = useState(null);

  /**
   * A section with no list sidebar (saved messages) has nothing for the pane to
   * cover, so its pane is always open — otherwise the phone stylesheet hides the
   * only thing the route renders and the screen comes up blank.
   */
  const paneIsWholeScreen = !Sidebar;
  const paneOpen = paneIsWholeScreen || (hasDetail && dismissedKey !== detailKey);

  const openPane = useCallback(() => setDismissedKey(null), []);

  const closePane = useCallback(() => {
    // Nothing behind it to reveal, so there is nothing to close.
    if (paneIsWholeScreen) return;

    // A conversation is in the URL, so going back to the list is a navigation —
    // dismissing it in state would leave the address bar pointing at a pane
    // that is no longer on screen.
    if (isConversationRoute(location.pathname)) {
      navigate(ROUTES.MESSAGES);
      return;
    }
    if (isNoteRoute(location.pathname)) {
      navigate(ROUTES.NOTES);
      return;
    }
    setDismissedKey(detailKey);
  }, [detailKey, location.pathname, navigate, paneIsWholeScreen]);

  /**
   * Hardware/browser Back closes the pane the same way the header arrow does.
   * Conversations are exempt: their pane is a route, so Back already leaves it.
   * So is a pane that owns the whole screen — there is nothing under it to
   * return to, and arming the guard there would swallow the press that is
   * supposed to take the user out of the section.
   *
   * Keyed on the detail rather than armed as a plain boolean, so moving between
   * two settings sections re-arms the guard. Left armed across that navigation,
   * one Back would both step the route back AND close the pane — two things at
   * once, from one press.
   */
  const guardKey =
    isPhone && paneOpen && !paneIsWholeScreen && !isUrlAddressedPane(location.pathname)
      ? detailKey
      : null;

  useBackGuard(guardKey, closePane);

  const value = useMemo(
    () => ({
      isPhone,
      paneOpen,
      openPane,
      closePane,
      rightSideOpen,
      setRightSideOpen,
      rightSideOpen1,
      setRightSideOpen1,
      rightSideOpen2,
      setRightSideOpen2,
      selectedCall,
      setSelectedCall,
      selectedNotification,
      setSelectedNotification,
      selectedSaved,
      setSelectedSaved,
    }),
    [
      isPhone,
      paneOpen,
      openPane,
      closePane,
      rightSideOpen,
      rightSideOpen1,
      rightSideOpen2,
      selectedCall,
      selectedNotification,
      selectedSaved,
    ]
  );

  return (
    <ShellContext.Provider value={value}>
      {/* `is-pane-open` is what the phone stylesheet keys off to lift the pane
          over the list. Above that breakpoint it means nothing — both columns
          are on screen regardless. */}
      <div className={`chat-layout-wrapper ${paneOpen ? 'is-pane-open' : ''}`}>
        <div className="side-menu">
          <SideBar />
        </div>

        {Sidebar && (
          <div className="chat-leftsidebar">
            <Sidebar
              openPane={openPane}
              setRightSideOpen={setRightSideOpen}
              setRightSideOpen1={setRightSideOpen1}
              setRightSideOpen2={setRightSideOpen2}
              selectedCall={selectedCall}
              setSelectedCall={setSelectedCall}
              selectedNotification={selectedNotification}
              setSelectedNotification={setSelectedNotification}
              selectedSaved={selectedSaved}
              setSelectedSaved={setSelectedSaved}
            />
          </div>
        )}

        <Outlet />
      </div>
    </ShellContext.Provider>
  );
};

export default ChatShell;
